import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation, useNavigate } from "react-router-dom";

interface AuthenticatedRouteProps {
  children: JSX.Element;
}

export function AuthenticatedRoute({ children }: AuthenticatedRouteProps) {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
  } = useAuth0();

  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: location.pathname } });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, location.pathname]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const token = await getAccessTokenSilently();
        console.log("access token:", token);

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);

        const response = await fetch(`${process.env.HAHS_API_URL}/users/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        });
        clearTimeout(timeout);

        if (!response.ok) {
          setError(
            `Failed to fetch user info: ${response.status} ${response.statusText}`
          );
          return;
        }

        const result = await response.json();
        const adminBool = result.value.isAdmin;
        setIsAdmin(adminBool);
        setError(null);
      } catch (err: any) {
        if (err.name === "AbortError") {
          setError("Request timed out. Please try again.");
        } else {
          setError(
            "Network error. Please check your connection and try again."
          );
        }
        console.error("Failed to fetch user info", err);
      }
    };

    if (!isLoading && isAuthenticated && isAdmin === null && !error) {
      fetchUserDetails();
    }
  }, [isLoading, isAuthenticated, getAccessTokenSilently, error]);

  useEffect(() => {
    if (isAdmin === false) {
      navigate("/admin/access-denied", { replace: true });
    }
  }, [isAdmin, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <svg
          className="animate-spin h-8 w-8 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8z"
          />
        </svg>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center text-red-500 mt-8">
        <p>Error: {error}</p>
        <button
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded"
          onClick={() => {
            setError(null);
            setIsAdmin(null);
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return children;
}
